import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../app/api.js';

export type ConsentSeriesPoint = {
  ts: string;
  accept: number;
  adjust: number;
  reject: number;
};

export type ExperimentSummaryRow = {
  experiment: string;
  variant: string;
  sessions: number;
  acceptRate: number;
  p50DecisionMs: number | null;
  p95DecisionMs: number | null;
};

export type ConsentSeriesParams = {
  baseUrl: string;
  siteKey?: string;
  from: string;
  to: string;
  bucket?: 'day' | 'hour';
  region?: string;
};

export type ExperimentSummaryParams = {
  baseUrl: string;
  siteKey?: string;
  from: string;
  to: string;
  experiment?: string;
};

export async function getConsentTimeSeries(
  params: ConsentSeriesParams,
): Promise<ConsentSeriesPoint[]> {
  const url = new URL('/analytics/consent', params.baseUrl);
  url.searchParams.set('from', params.from);
  url.searchParams.set('to', params.to);
  if (params.siteKey) url.searchParams.set('siteKey', params.siteKey);
  if (params.region) url.searchParams.set('region', params.region);
  url.searchParams.set('bucket', params.bucket ?? 'day');
  return apiGet<ConsentSeriesPoint[]>(url.toString());
}

export async function getExperimentSummary(
  params: ExperimentSummaryParams,
): Promise<ExperimentSummaryRow[]> {
  const url = new URL('/analytics/experiments', params.baseUrl);
  url.searchParams.set('from', params.from);
  url.searchParams.set('to', params.to);
  if (params.siteKey) url.searchParams.set('siteKey', params.siteKey);
  if (params.experiment) url.searchParams.set('experiment', params.experiment);
  return apiGet<ExperimentSummaryRow[]>(url.toString());
}

export function useConsentSeries(
  params: ConsentSeriesParams | undefined,
): UseQueryResult<ConsentSeriesPoint[], Error> {
  return useQuery({
    queryKey: ['analytics', 'consent', params],
    queryFn: () => {
      if (!params) throw new Error('params required');
      return getConsentTimeSeries(params);
    },
    enabled: Boolean(params?.from && params?.to),
    staleTime: 60_000,
  });
}

export function useExperimentSummary(
  params: ExperimentSummaryParams | undefined,
): UseQueryResult<ExperimentSummaryRow[], Error> {
  return useQuery({
    queryKey: ['analytics', 'experiments', params],
    queryFn: () => {
      if (!params) throw new Error('params required');
      return getExperimentSummary(params);
    },
    enabled: Boolean(params?.from && params?.to),
    staleTime: 60_000,
  });
}
